import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  ChevronRightIcon,
  DownloadIcon,
  FileIcon,
  FilePenIcon,
  FileTextIcon,
  FolderIcon,
  ImageIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import type { PortalDocument, PortalFolder } from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { PortalDocumentSheet } from '@/components/portal/portal-document-sheet'
import { SearchInput } from '@/components/search-input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { downloadDocument } from '@/hooks/use-document-content'
import { msg } from '@/i18n'
import { compareFolderNames } from '@/lib/folders'
import { formatBytes, formatDate } from '@/lib/format'
import { matching, readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/portal/documents')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch(
      {
        /** Lot 20 : le dossier ouvert, absent à la racine. */
        dossier: matching(/^[0-9a-f]{24}$/),
        /** #250 : le document ouvert dans le panneau — un lien le rouvre. */
        document: matching(/^[0-9a-f]{24}$/),
        /** 1.50.0 : les documents d'un projet, à plat — le bloc « documents » d'un projet y mène. */
        projet: matching(/^[0-9a-f]{24}$/),
      },
      search,
    ),
  component: PortalDocumentsPage,
})

function DocumentIcon({ document }: { document: PortalDocument }) {
  const mime = document.mime_type ?? ''
  if (document.kind === 'markdown') {
    return <FilePenIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  }
  if (document.kind === 'screenshot' || mime.startsWith('image/')) {
    return <ImageIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  }
  if (mime.startsWith('text/') || mime === 'application/pdf') {
    return <FileTextIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  }
  return <FileIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
}

function PortalDocumentsPage() {
  const { dossier, document: openId, projet } = Route.useSearch()
  const navigate = Route.useNavigate()
  const t = msg().portalRequests.documents
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query)

  /**
   * Lot 20 : les dossiers partagés qui mènent à au moins un document partagé.
   * Tant que l'API ne les sert pas, la page reste une liste : `folder_id`
   * serait refusé.
   */
  const folders = useQuery({
    queryKey: ['portal', 'folders'],
    queryFn: () => portalApi.folders(),
    retry: false,
  })
  // Les documents d'un projet se lisent à plat : le filtre traverse les dossiers.
  const foldersOn = folders.isSuccess && projet === undefined
  const folderList = folders.data ?? []
  const current =
    foldersOn && dossier ? (folderList.find((folder) => folder.id === dossier) ?? null) : null
  const folderGone = foldersOn && dossier !== undefined && current === null
  const openFolder = (id: string | null) => {
    setQuery('')
    void navigate({ search: id ? { dossier: id } : {} })
  }
  /** Le panneau s'ouvre et se ferme par l'URL : le dossier ouvert reste en place. */
  const openDocument = (id: string | undefined) => {
    void navigate({ search: (previous) => ({ ...previous, document: id }) })
  }

  // Une recherche parcourt tous les documents partagés ; sinon, le dossier ouvert.
  const params = {
    q: debouncedQuery || undefined,
    page_size: 100,
    ...(projet ? { project_id: projet } : {}),
    ...(foldersOn && !debouncedQuery ? { folder_id: current?.id ?? 'none' } : {}),
  }
  const projects = useQuery({
    queryKey: ['portal', 'projects'],
    queryFn: () => portalApi.projects(),
    enabled: projet !== undefined,
  })
  const projectName = projects.data?.find((project) => project.id === projet)?.name
  const documents = useQuery({
    queryKey: ['portal', 'documents', params],
    queryFn: () => portalApi.documents(params),
    placeholderData: (previous) => previous,
    enabled: !folderGone,
  })
  const subfolders = folderList
    .filter((folder) => folder.parent_id === (current?.id ?? null))
    .sort((a, b) => compareFolderNames(a.name, b.name))

  const items = documents.data?.items ?? []
  const filtered = debouncedQuery !== ''

  const open = (document: PortalDocument) => {
    openDocument(document.id)
  }

  return (
    <div className="space-y-5">
      <PageHeader title={t.title} />

      <Frame>
        {projet && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm"
            data-testid="documents-du-projet"
          >
            <span className="min-w-0 flex-1">
              {t.projectDocuments} <span className="font-medium">{projectName ?? '…'}</span>
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigate({ search: {} })
              }}
            >
              {t.allDocuments}
            </Button>
          </div>
        )}
        {foldersOn && folderList.length > 0 && (
          <PortalFolderTrail folders={folderList} current={current} onOpen={openFolder} />
        )}
        {current?.description && (
          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{current.description}</p>
        )}
        {folderGone && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm text-muted-foreground"
            data-testid="dossier-indisponible"
          >
            <span className="min-w-0 flex-1">{t.folderGone}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                openFolder(null)
              }}
            >
              {t.backToDocuments}
            </Button>
          </div>
        )}
        {!debouncedQuery && subfolders.length > 0 && (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="portal-dossiers">
            {subfolders.map((folder) => (
              <li key={folder.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => {
                    openFolder(folder.id)
                  }}
                >
                  <FolderIcon
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{folder.name}</span>
                  {folder.document_count > 0 && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {t.count(folder.document_count)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {(items.length > 0 || folderList.length > 0 || filtered) && !folderGone && (
          <SearchInput
            value={query}
            onChange={setQuery}
            label={t.searchLabel}
            placeholder={t.searchPlaceholder}
            className="w-full max-w-xs"
          />
        )}

        {folderGone ? null : documents.isPending ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : documents.isError ? (
          <EmptyState
            icon={FileIcon}
            title={t.loadFailed}
            description={
              documents.error instanceof ApiError ? documents.error.message : t.retryLater
            }
            action={
              <Button
                variant="outline"
                onClick={() => {
                  void documents.refetch()
                }}
              >
                {t.retry}
              </Button>
            }
          />
        ) : items.length === 0 ? (
          // Un dossier qui ne porte que des sous-dossiers n'a rien à dire de plus.
          subfolders.length > 0 && !filtered ? null : (
            <EmptyState
              icon={FileIcon}
              title={filtered ? t.emptyFiltered : t.empty}
              description={filtered ? t.emptyFilteredHint : undefined}
            />
          )
        ) : (
          <ul className="divide-y rounded-lg border">
            {items.map((document) => (
              <li
                key={document.id}
                className="flex flex-wrap items-center gap-3 px-3 py-3"
                data-testid="portal-document"
              >
                <DocumentIcon document={document} />
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left font-medium hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => {
                    open(document)
                  }}
                >
                  {document.name}
                </button>

                {document.kind === 'screenshot' && <Badge variant="muted">{t.screenshot}</Badge>}
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  {formatBytes(document.size_bytes)}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatDate(document.created_at)}
                </span>

                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t.download(document.name)}
                  disabled={!document.content_url}
                  onClick={() => {
                    if (!document.content_url) return
                    void downloadDocument(document.content_url, document.name).catch(() => {
                      toast.error(t.downloadFailed)
                    })
                  }}
                >
                  <DownloadIcon className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <PortalDocumentSheet
          documentId={openId ?? null}
          onClose={() => {
            openDocument(undefined)
          }}
        />
      </Frame>
    </div>
  )
}

/**
 * Le fil d'Ariane du portail : `PortalFolder` ne porte pas de chemin, on le
 * remonte par `parent_id` — toujours un dossier servi, ou nul.
 */
function PortalFolderTrail({
  folders,
  current,
  onOpen,
}: {
  folders: readonly PortalFolder[]
  current: PortalFolder | null
  onOpen: (id: string | null) => void
}) {
  const t = msg().portalRequests.documents
  const trail: PortalFolder[] = []
  let cursor = current
  while (cursor) {
    trail.unshift(cursor)
    const parentId: string | null = cursor.parent_id
    cursor = parentId === null ? null : (folders.find((folder) => folder.id === parentId) ?? null)
  }
  return (
    <nav aria-label={t.folderTrail}>
      <ol className="flex flex-wrap items-center gap-1 text-sm" data-testid="portal-fil-dossiers">
        <li>
          {current ? (
            <button
              type="button"
              className="font-medium text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => {
                onOpen(null)
              }}
            >
              {t.allDocuments}
            </button>
          ) : (
            <span className="font-medium" aria-current="page">
              {t.allDocuments}
            </span>
          )}
        </li>
        {trail.map((folder) => (
          <li key={folder.id} className="flex items-center gap-1">
            <ChevronRightIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
            {folder.id === current?.id ? (
              <span className="font-medium" aria-current="page">
                {folder.name}
              </span>
            ) : (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground hover:underline"
                onClick={() => {
                  onOpen(folder.id)
                }}
              >
                {folder.name}
              </button>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
